// External libraries
import React from 'react';
// @ts-expect-error untyped import
import { Interstitial, PayPalMonogram } from '@checkout/react-components';

// Internal modules
import { getContent, getLocale, logger } from '@/lib';
import { useMountLogging } from '@/lib/reactHooks';
import { FLAG_SPRITE_POSITION_MAP } from '@/styles';
import { handleCountryChange } from '../shared';
import {
  Container,
  CountryGroupContainer,
  CountryGroupHeader,
  CountryLink,
  CountryList,
  CountryListItem,
  FlagIcon,
  InterstitialFooter,
} from './StyledComponents';
import {
  interstitialHeaderStyleOverrides,
  interstitialStyleOverrides,
} from './styles';
import { useShippingAddressCallbacks } from '@/hooks/shippingCallbacks';
import type { GenericObjectType } from '@/types';
import { isValidCountrySelection } from '../shared/helpers';
import { CloseSmIcon } from '@/components/Common';

type CountryChangeInterstitialProps = {
  handleClose?: () => void;
  territoriesByRegion: {
    AMERICAS: GenericObjectType[];
    APAC: GenericObjectType[];
    EMEA: GenericObjectType[];
  };
};
const CountryChangeInterstitial = (
  props: CountryChangeInterstitialProps,
): JSX.Element => {
  useMountLogging('CountryChangeInterstitial');
  const onShippingAddressCallback = useShippingAddressCallbacks();
  const { handleClose, territoriesByRegion } = props;
  const { country } = getLocale();

  return (
    <Interstitial
      headerStyleOverrides={interstitialHeaderStyleOverrides}
      interstitialStyleOverrides={interstitialStyleOverrides}
      onClose={handleClose}
      withoutPayPalLogo={true}
      closeButtonContent={<CloseSmIcon />}
    >
      <Container>
        {Object.entries(territoriesByRegion).map(([region, countries]) => (
          <CountryGroupContainer key={region}>
            <CountryGroupHeader>
              {getContent(`regions.${region}`)}
            </CountryGroupHeader>
            <CountryList>
              {countries.map(({ code, name }) => {
                if (code === country) {
                  return null;
                }

                const handleChange = (e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();

                  /**
                   * @see https://paypal.atlassian.net/browse/LI-4008
                   * Auto-fill features, such as Chrome, appear to be selecting
                   * out-of-range values from the select causing an unexpected
                   * crash.
                   *
                   * In the event of an out-of-range selection the toCountry
                   * will be and empty string and will fail validation. We log
                   * the values for debugging and return to prevent the handlers
                   * from being called which would otherwise crash.
                   */
                  if (!isValidCountrySelection('code', code, countries)) {
                    logger.info('weasley_interstitial_country_invalid_value', {
                      fromCountry: country,
                      toCountry: code,
                    });
                    return;
                  }

                  handleCountryChange(
                    { fromCountry: country, toCountry: code },
                    onShippingAddressCallback,
                  );
                };

                return (
                  <CountryListItem key={code}>
                    <CountryLink href="#" onClick={handleChange}>
                      <FlagIcon bgPosition={FLAG_SPRITE_POSITION_MAP[code]} />
                      <span>{name}</span>
                    </CountryLink>
                  </CountryListItem>
                );
              })}
            </CountryList>
          </CountryGroupContainer>
        ))}
      </Container>
      <InterstitialFooter>
        <PayPalMonogram />
      </InterstitialFooter>
    </Interstitial>
  );
};

export { CountryChangeInterstitial };
